// @mui
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
// routes
import { paths } from 'src/routes/paths';
import { useParams } from 'src/routes/hook';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Image from 'src/components/image';
import Lightbox, { useLightBox } from 'src/components/lightbox';
//
import { usePortal } from 'src/hooks/use-portal';
import { useMemo } from 'react';
//
import PostDetailsHero from '../post-details-hero';
import { PostDetailsSkeleton } from '../post-skeleton';

// ----------------------------------------------------------------------

export default function CategoryDetailsHomeView({ isRTL }: any) {
  const params = useParams();

  const { id } = params;
  const { categories, products: items, loading } = usePortal({ categoryId: id });
  const category = useMemo(() => categories.find((cat: any) => cat._id === id), [categories, id]);

  // Prepare images for lightbox
  const images = useMemo(
    () => items.map((item: any) => ({ src: item.photo, alt: isRTL ? item.nameAr : item.name })),
    [items, isRTL]
  );

  const lightbox = useLightBox(images);

  const renderSkeleton = <PostDetailsSkeleton />;
  const renderPost = category && (
    <>
      <PostDetailsHero
        title={category.name}
        author={category.sectionName}
        coverUrl={category.headimage}
        createdAt={category.createdAt}
      />

      <Container
        sx={{
          py: 3,
          mb: 5,
          borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      >
        <CustomBreadcrumbs
          links={[
            {
              name: isRTL ? 'الرئيسية' : 'Home',
              href: '/',
            },
            {
              name: isRTL ? 'الفئات' : 'Services',
              href: paths.category.root,
            },
            {
              name: isRTL ? category.nameAr : category.name,
            },
          ]}
          sx={{ mx: 'auto' }}
        />
      </Container>

      <Container sx={{ mb: 5 }}>
        <Stack sx={{ mx: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 5 }}>
            {category?.desc}
          </Typography>
        </Stack>

        {/* Instagram-style Photo Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: { xs: 1, md: 2 },
          }}
        >
          {items.map((item: any, index: number) => (
            <Box
              key={item._id}
              onClick={() => lightbox.onOpen(item.photo)}
              sx={{
                position: 'relative',
                aspectRatio: '1/1',
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover': {
                  '& .overlay': {
                    opacity: 1,
                  },
                  '& img': {
                    transform: 'scale(1.05)',
                  },
                },
              }}
            >
              <Image
                alt={isRTL ? item.nameAr : item.name}
                src={item.photo}
                sx={{
                  width: '100%',
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '& img': {
                    objectFit: 'cover',
                  },
                }}
              />
              <Box
                className="overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                }}
              />
            </Box>
          ))}
        </Box>
      </Container>

      <Lightbox
        index={lightbox.selected}
        slides={images}
        open={lightbox.open}
        close={lightbox.onClose}
      />
    </>
  );

  return <>{loading ? renderSkeleton : renderPost}</>;
}
